import { motion } from "framer-motion";
import { CheckCircle, Leaf, Sun, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const features = [
  {
    icon: CheckCircle,
    title: "Multiple Hybrid Varieties",
    description: "GD, Gangabodam & COD × WCT crosses",
  },
  {
    icon: Leaf,
    title: "Healthy Seedlings",
    description: "Raised under trained supervision",
  },
  {
    icon: Sun,
    title: "Plant Vigor",
    description: "Focus on growth and health",
  },
  {
    icon: Droplets,
    title: "Quality Assured",
    description: "Following best practices",
  },
];

const varieties = [
  {
    name: "GD × WCT Crossed Plants",
    detail: "Green Dwarf × West Coast Tall hybrid seedlings",
    price: "₹550",
    priceNote: "per seedling",
  },
  {
    name: "Gangabodam × WCT Crossed Plants",
    detail: "Gangabodam Dwarf × West Coast Tall hybrid seedlings",
    price: "Contact for price",
    priceNote: "on inquiry",
  },
  {
    name: "COD × WCT Crossed Plants",
    detail: "Chowghat Orange Dwarf × West Coast Tall hybrid seedlings",
    price: "Contact for price",
    priceNote: "on inquiry",
  },
];

export default function ProductsSection() {
  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="products"
      className="py-20 md:py-32 bg-white"
      data-testid="products-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sm font-medium tracking-widest uppercase text-[#E07A5F] mb-4 block">
            Our Products
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A1C18] mb-6 tracking-tight"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Hybrid Coconut Seedlings
          </h2>
          <p className="text-[#4A4F45] text-lg leading-relaxed">
            We specialize in producing high-quality hybrid coconut seedlings,
            carefully nurtured to ensure maximum yield and disease resistance.
          </p>
        </motion.div>

        {/* Product Card */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="img-zoom rounded-3xl overflow-hidden"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/mzsof7kc_IMG20260202172617.jpg"
              alt="Hybrid Coconut Seedlings at Kishan Coconut Nursery"
              className="w-full h-[400px] md:h-[500px] object-cover"
            />
          </motion.div>

          {/* Features */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
          >
            <h3
              className="text-2xl md:text-3xl font-medium text-[#1A1C18] mb-8"
              style={{ fontFamily: "'Fraunces', serif" }}
            >
              Hybrid Coconut Seedlings
            </h3>

            <div className="space-y-6 mb-8">
              {features.map((feature, index) => (
                <div key={feature.title} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F0EFE9] rounded-2xl flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-[#2F5233]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A1C18] mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-[#8C9187] text-sm">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Price Card */}
            <div className="p-6 bg-[#2F5233] rounded-2xl mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm mb-1">GD × WCT Price per Seedling</p>
                  <p className="text-3xl font-bold text-white" style={{ fontFamily: "'Fraunces', serif" }}>
                    ₹550
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[#81B29A] text-sm">Other Varieties</p>
                  <p className="text-white/70 text-xs">Price on inquiry</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-[#F2CC8F]/20 rounded-2xl border border-[#F2CC8F]/30 mb-8">
              <p className="text-[#4A4F45] text-sm">
                <strong>Note:</strong> Availability subject to season and stock.
                Contact us for current availability and bulk orders.
              </p>
            </div>

            <Button
              onClick={scrollToContact}
              className="bg-[#2F5233] hover:bg-[#1E3621] text-white rounded-full px-8 py-6 text-lg font-medium btn-scale shadow-lg shadow-[#2F5233]/20"
              data-testid="products-enquiry-btn"
            >
              Enquire Now
            </Button>
          </motion.div>
        </div>

        {/* Available Cross Varieties */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="mt-16"
          data-testid="varieties-block"
        >
          <div className="text-center mb-8">
            <span className="text-sm font-medium tracking-widest uppercase text-[#E07A5F] mb-2 block">
              Available Varieties
            </span>
            <h3
              className="text-2xl md:text-3xl font-semibold text-[#1A1C18]"
              style={{ fontFamily: "'Fraunces', serif" }}
            >
              Hybrid Cross Plants We Offer
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {varieties.map((variety, index) => (
              <div
                key={variety.name}
                className="p-8 bg-[#F9F8F4] rounded-3xl border border-[#E5E2D9] text-center"
                data-testid={`variety-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#2F5233] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <h4
                  className="text-lg font-semibold text-[#1A1C18] mb-2"
                  style={{ fontFamily: "'Fraunces', serif" }}
                >
                  {variety.name}
                </h4>
                <p className="text-[#8C9187] text-sm leading-relaxed mb-4">
                  {variety.detail}
                </p>
                <div className="pt-4 border-t border-[#E5E2D9]">
                  <p
                    className="text-2xl font-bold text-[#2F5233]"
                    style={{ fontFamily: "'Fraunces', serif" }}
                  >
                    {variety.price}
                  </p>
                  <p className="text-xs text-[#8C9187]">{variety.priceNote}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
